// #3583 (REGDEC.6b, D5 b) -- one line after a SAD save that touched section 10.2: what the save did to the open-question
// register, in counts. A separate event from register_edit_report on purpose: that banner shows nothing when no edit was
// rejected or overridden, and stays unchanged.

export interface RegisterDeltaInfo {
  added: number;
  matched: number;
  ambiguous: number;
  held: number;
  /** True only when the agent's next-turn notice about held questions was really recorded. */
  agentNotified: boolean;
}

const count = (v: unknown): number => (typeof v === 'number' && Number.isFinite(v) && v >= 0 ? Math.trunc(v) : 0);

export function parseRegisterDeltaEvent(data: { type: string; added?: number; matched?: number; ambiguous?: number; held?: number; agentNotified?: boolean }): RegisterDeltaInfo | null {
  if (data.type !== 'register_delta') return null;
  const info = { added: count(data.added), matched: count(data.matched), ambiguous: count(data.ambiguous), held: count(data.held), agentNotified: data.agentNotified === true };
  return info.added + info.ambiguous + info.held === 0 ? null : info; // matched alone: nothing changed, nothing to say
}

export function registerDeltaText(info: RegisterDeltaInfo): string {
  const parts = [`${info.added} added`, `${info.matched} already tracked`];
  if (info.ambiguous > 0) parts.push(`${info.ambiguous} close to an existing question (not added)`);
  if (info.held > 0) parts.push(`${info.held} held (more new questions than one save may add; none were created)`);
  return `Open questions: ${parts.join(', ')}.`;
}

export function RegisterDeltaLine({ info }: { info: RegisterDeltaInfo }) {
  const held = info.held > 0;
  return (
    <p
      role={held ? 'alert' : 'status'}
      data-testid="register-delta-line"
      className={held ? 'mx-6 mt-3 text-sm text-amber-800 flex-shrink-0' : 'mx-6 mt-3 text-sm text-gray-600 flex-shrink-0'}
    >
      {registerDeltaText(info)}
      {held && (info.agentNotified ? ' The agent has been told and will ask you which to keep.' : ' Tell the agent to send them in smaller groups.')}
    </p>
  );
}
